import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from 'react';
import { useRef } from 'react';

interface UseFocusManagementProps {
  chips: string[];
  setChips: Dispatch<SetStateAction<string[]>>;
  inputRef: RefObject<HTMLInputElement>;
}

interface UseFocusManagementReturn {
  chipRefs: MutableRefObject<(HTMLElement | null)[]>;
  setChipRef: (element: HTMLElement | null, index: number) => void;
  handleRemoveChip: (trackingId: string, index: number) => void;
}

export const useFocusManagement = ({
  chips,
  setChips,
  inputRef,
}: UseFocusManagementProps): UseFocusManagementReturn => {
  const chipRefs = useRef<(HTMLElement | null)[]>([]);

  // Initialize refs array when chips change
  if (chipRefs.current.length !== chips.length) {
    chipRefs.current = Array(chips.length).fill(null);
  }

  const setChipRef = (element: HTMLElement | null, index: number) => {
    chipRefs.current[index] = element;
  };

  const handleRemoveChip = (trackingId: string, index: number) => {
    const modifyChips = chips.filter((chip) => chip !== trackingId);
    setChips(modifyChips);

    // Focus management after removal
    setTimeout(() => {
      if (index < modifyChips.length) {
        // If there's a next chip, focus it
        chipRefs.current[index]?.focus();
      } else if (modifyChips.length > 0) {
        // If we removed the last chip but there are still chips, focus the new last chip
        chipRefs.current[modifyChips.length - 1]?.focus();
      } else {
        // If we removed the last remaining chip, focus the input
        inputRef.current?.focus();
      }
    }, 0);
  };

  return { chipRefs, setChipRef, handleRemoveChip };
};
